import { HttpClient } from "@angular/common/http";
import { Inject, Injectable } from "@angular/core";
import { HotToastService } from "@ngneat/hot-toast";
import { Observable } from "rxjs";
import { Filter } from "../../_shared/components/table/sortable-table/filter-form-group";
import { BasicDto } from "../../_shared/models/basicDto";
import { OdataList } from "../../_shared/models/odataList";
import { MailService } from "../../_shared/services/mail.service";
import { FederationBasicDto } from "../models/federation";
import {
  OrganizationPublic,
  OrganizationWithAddressDto,
  OrganizationWithAddressesDto,
} from "../models/organization";
import {
  AdjustTrainingHoursSaldoRequest,
  AdjustTrainingHoursSaldoResponse,
} from "../models/training-hours-saldo";

@Injectable({
  providedIn: "root",
})
export class OrganizationService extends MailService<
  OrganizationWithAddressesDto,
  OrganizationPublic
> {
  constructor(
    protected override http: HttpClient,
    @Inject("BASE_URL") protected override baseUrl: string
  ) {
    super(http, baseUrl, "organization");
  }

  getByKey(key: string): Observable<OrganizationPublic> {
    return this.get("/bykey/" + key);
  }

  getCurrentFederation(): Observable<FederationBasicDto> {
    return this.get("/currentfederation");
  }

  getAllForFederation(id: number, accessKey: string): Observable<OrganizationWithAddressDto[]> {
    return this.getAll<OrganizationWithAddressDto>(
      "/byFederation/" + id + "?accessKey=" + accessKey
    );
  }

  getAllChildren(orgKey: number) {
    return this.getAllOdata("/current/children/" + orgKey + "?searchTerm=&listType=1&$top=10000");
  }

  getChildren(
    subgroupId: number,
    filter: Filter
  ): Observable<OdataList<OrganizationWithAddressDto>> {
    return this.getAllOdata(
      "/current/children/" + subgroupId + "?" + this.serializeForQueryStringOdata(filter)
    );
  }

  getVZFClubs() {
    return this.getAllOdata(
      "/vzfclubs?clubCode=&name=&postalCode=&province=0&sportsId=0&$orderby=ClubCode"
    );
  }

  getAccesKey(): Observable<string> {
    return this.get<string>("/current/accessKey");
  }

  removeFromFederation(organizationId: number, federationGroupId: number) {
    return this.http.delete(
      `${this.baseUrl}organizationRelation/byChildId/${organizationId}/${federationGroupId}`
    );
  }

  getValuationRules() {
    return this.get<any>("/appreciationrules");
  }

  getFoundedForOptions() {
    return this.http.get<OdataList<BasicDto<number>[]>>(`${this.baseUrl}foundedFor`);
  }

  sendMailsToAllChildren(filterFields: Filter, template: any, subGroupId: number) {
    return this.post<any, number>(
      `/current/children/${subGroupId}/mail?allContactPersons=true&` +
        this.serializeForQueryStringOdata(filterFields),
      template
    );
  } //TODO: is allContactPersons correct

  override sendMailsToAll(filterFields: Filter, template: any): Observable<number> {
    throw new Error("Should not use this method. Use sendMailsToAllChildren instead.");
  }

  override sendMailsToSelection(filterFields: Filter, template: any): Observable<number> {
    throw new Error("Should not use this method. Use sendMailsToSelectionChildren instead.");
  }
  override sendMail(recipientId: number, template: any) {
    return this.post(`/${recipientId}/mail?allContactPersons=true`, template);
  }

  adjustTrainingHoursSaldo(
    organizationId: number,
    request: AdjustTrainingHoursSaldoRequest
  ): Observable<AdjustTrainingHoursSaldoResponse> {
    return this.post<AdjustTrainingHoursSaldoRequest, AdjustTrainingHoursSaldoResponse>(
      `/${organizationId}/trainingHoursSaldo`,
      request
    );
  }

  exportChildrenRegister(subgroupId: number, filter: Filter, toast: HotToastService, ids?: number[]): void {
    const f = filter as any;
    let url = `${this.baseUrl}${this.controller}/current/children/${subgroupId}/export?searchTerm=${encodeURIComponent(f.SearchTerm ?? "")}&listType=${f.ListType ?? 1}&exportType=register&workingYearId=&payed=&hasMembers=`;
    if (ids?.length) {
      url += "&" + ids.map((id) => `ids=${id}`).join("&");
    }
    let download$ = this.http.get(url, { responseType: "blob", observe: "response" });
    if (toast) {
      download$ = download$.pipe(
        toast.observe({
          loading: "Bezig met downloaden",
          success: "Gedownload",
          error: "Iets ging mis",
        })
      );
    }
    download$.subscribe((response) => {
      const disposition = response.headers.get("content-disposition");
      const match = disposition?.match(/filename="?([^"]+)"?/);
      const filename = match?.[1] ?? "export-verenigingen.xlsx";
      const blob = response.body!;
      const blobUrl = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = blobUrl;
      link.download = filename;
      link.click();
      URL.revokeObjectURL(blobUrl);
    });
  }
}
